// client/src/components/Insights/shared/InsightsDateFilter.jsx
// Compact date-range bar shown above the tabs on the Keeper AI Insights page.

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Button, TextField, Typography, useTheme, useMediaQuery } from '@mui/material';
import { CalendarRange } from 'lucide-react';
import { GlassCard } from './SharedComponents';
import { GRADIENT, alpha } from './constants';
import { useInsightsFilter, PRESET_KEYS } from './InsightsFilterContext';

const PRESET_LABELS = {
  '7d':     { en: '7D',     key: 'aiInsights.filters.preset7d' },
  '30d':    { en: '30D',    key: 'aiInsights.filters.preset30d' },
  '90d':    { en: '90D',    key: 'aiInsights.filters.preset90d' },
  '6m':     { en: '6M',     key: 'aiInsights.filters.preset6m' },
  '12m':    { en: '12M',    key: 'aiInsights.filters.preset12m' },
  'ytd':    { en: 'YTD',    key: 'aiInsights.filters.presetYtd' },
  'custom': { en: 'Custom', key: 'aiInsights.filters.presetCustom' },
};

const humanRange = (from, to, t) => {
  if (!from || !to) return '';
  const f = new Date(from);
  const e = new Date(to);
  if (isNaN(f.getTime()) || isNaN(e.getTime())) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${f.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}`;
};

const InsightsDateFilter = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { from, to, preset, setPreset, setRange } = useInsightsFilter();
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo]     = useState(to);

  useEffect(() => { setDraftFrom(from); setDraftTo(to); }, [from, to]);

  const dirty = draftFrom !== from || draftTo !== to;

  const apply = () => {
    if (!draftFrom || !draftTo) return;
    setRange(draftFrom, draftTo, 'custom');
  };

  return (
    <GlassCard sx={{ mb: 2, p: { xs: 1.5, md: 2 } }}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.5}
        alignItems={isMobile ? 'stretch' : 'center'}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: 2, background: GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <CalendarRange size={14} color="#fff" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
              {t('aiInsights.filters.label', 'Date range')}
            </Typography>
            <Typography variant="body2" fontWeight={700} noWrap>
              {humanRange(from, to, t)}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {PRESET_KEYS.map((key) => {
            const active = preset === key;
            const label = t(PRESET_LABELS[key].key, PRESET_LABELS[key].en);
            return (
              <Button
                key={key}
                size="small"
                onClick={() => setPreset(key)}
                sx={{
                  minWidth: 0, px: 1.25, py: 0.25, borderRadius: 2,
                  fontWeight: 700, fontSize: 11, textTransform: 'none',
                  background: active ? GRADIENT : 'transparent',
                  color: active ? '#fff' : 'text.secondary',
                  border: active ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                  '&:hover': { background: active ? GRADIENT : alpha('#8b5cf6', 0.08) },
                }}
              >
                {label}
              </Button>
            );
          })}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
          <TextField
            type="date"
            size="small"
            value={draftFrom}
            onChange={(e) => setDraftFrom(e.target.value)}
            label={t('aiInsights.filters.from', 'From')}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '48%', md: 160 } }}
          />
          <TextField
            type="date"
            size="small"
            value={draftTo}
            onChange={(e) => setDraftTo(e.target.value)}
            label={t('aiInsights.filters.to', 'To')}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '48%', md: 160 } }}
          />
          <Button
            size="small"
            disabled={!dirty}
            onClick={apply}
            sx={{
              fontWeight: 700, textTransform: 'none', borderRadius: 2,
              background: dirty ? GRADIENT : 'transparent',
              color: dirty ? '#fff' : 'text.secondary',
              border: dirty ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              px: 2,
              '&:hover': { background: dirty ? GRADIENT : 'transparent', opacity: 0.9 },
            }}
          >
            {t('aiInsights.filters.apply', 'Apply')}
          </Button>
        </Stack>
      </Stack>
    </GlassCard>
  );
};

export default InsightsDateFilter;
